<template>
  <Layout class="customer-contract-form-layout">
    <template #header-container>
      <PageHeaderV2 hasBreadcrumb :items="breadcrumbItems" :customTitle="true" isSticky>
        <template v-if="customerContractStatus === 'Draf'" #banner>
          <div class="mc-outline-info d-flex p-3 gap-2 align-items-center">
            <i class="mc-fa icon-fill_info" />
            <span class="teal-600"
              >Unggah <b>minimal 1 dokumen pendukung</b> untuk mengubah status kontrak menjadi
              <b>Dikonfirmasi</b> secara otomatis.</span
            >
          </div>
        </template>

        <template #afterTitle>
          <div class="d-flex gap-2">
            <McButton
              variant="secondary"
              class="ms-3"
              icon="icon-outline_arrow-left"
              :text="$t('global.back')"
              :disabled="isLoading"
              @click="$router.replace({ name: 'Master Contract', query: { type: 'customer' } })"
            />
            <McButton
              v-if="['Draf', 'Dikonfirmasi'].includes(customerContractStatus)"
              v-b-tooltip.hover.bottom="{
                title: 'Tambahkan Plat Nomor kendaraan untuk mengakses fitur ini',
                disabled: hasTrackLink,
              }"
              variant="secondary"
              icon="icon-outline_share"
              text="Bagikan"
              :disabled="!hasTrackLink || isLoading"
              @click="handleTrackVehicle"
            />
            <OtherOptions v-if="customerContractStatus !== 'Temporary'" :disabled="isLoading" />
            <McTransitionStatus
              variant="secondary"
              :isCreating="isCreatingContract"
              :entityData="customerContract"
              :statusTransitions="statusTransitions"
              :disabled="isLoading"
              @transition="handleTransitionStatus"
            />
            <McButton
              v-if="['Dikonfirmasi', 'Kadaluwarsa', 'Ditutup'].includes(customerContractStatus)"
              text="Perbarui Kontrak"
              icon="icon-fill_sync"
              :disabled="isLoading"
              @click="$refs['mc-modal-renewal-contract'].show()"
            />
          </div>
        </template>

        <template #title>
          <template v-if="!isCreatingContract">
            <b-skeleton-wrapper :loading="isLoading">
              <template #loading>
                <div class="d-flex items-center">
                  <h4>Detail Kontrak Pelanggan</h4>
                  <b-skeleton class="mb-0 ms-2" width="100px" height="23px" />
                </div>
              </template>
              <h4>{{ title }}</h4>
            </b-skeleton-wrapper>
          </template>
          <template v-else>
            <h4>{{ title }}</h4>
          </template>
        </template>

        <template #footer>
          <b-skeleton-wrapper :loading="isLoading">
            <template #loading>
              <b-skeleton width="400px" height="20px" />
            </template>
            <McWizard compact :items="wizardItems" :activeKey="customerContractStatus" />
          </b-skeleton-wrapper>
        </template>
      </PageHeaderV2>
    </template>
    <FormulateForm
      ref="formRef"
      v-model="formValues"
      name="customer-contract-form"
      class="mt-4"
      :errors="formErrors"
    >
      <div class="d-flex align-items-center justify-content-between mb-3">
        <h5>Informasi Detail</h5>
        <div v-if="['Temporary', 'Draf', 'Dikonfirmasi'].includes(customerContractStatus)">
          <McButton
            class="me-2"
            variant="secondary"
            icon="icon-outline_close"
            :text="$t('global.action.cancel')"
            :disabled="!hasChanged"
            @click="$refs['mc-modal-cancel'].show()"
          />
          <b-modal
            id="mc-modal-cancel"
            ref="mc-modal-cancel"
            class="mc-modal"
            :title="$t('global.action.cancel')"
            size="md"
            centered
            scrollable
          >
            <div class="card-body p-2">
              <p>
                Apakah Anda yaking ingin membatalkan perubahan
                <strong>Informasi Kontrak Pelanggan</strong>?
              </p>
            </div>
            <template #modal-footer="{ cancel }">
              <div class="d-flex mx-0">
                <McButton
                  variant="secondary"
                  class="me-2"
                  :text="$t('global.action.no')"
                  @click="cancel"
                />
                <McButton variant="primary" :text="$t('global.action.yes')" @click="handleCancel" />
              </div>
            </template>
          </b-modal>
          <McButton
            variant="primary"
            text="Simpan"
            icon="icon-outline_save"
            :disabled="!hasChanged"
            @click="handleSubmit"
          />
        </div>
      </div>
      <b-card>
        <h5>Informasi Dasar</h5>
        <b-row>
          <b-col v-for="field in baseInfoFields" :key="field.key" md="3" lg="3">
            <FormulateInput
              v-bind="field"
              :fullObject="fullObjects[field.name]"
              @update:fullObject="($event) => (fullObjects[field.name] = $event)"
            >
              <template #element="{ attributes, model }">
                <b-skeleton-wrapper :loading="isLoading">
                  <template #loading>
                    <b-skeleton height="37px" />
                  </template>
                  <template v-if="isNotEditable || attributes?.readOnly">
                    <template v-if="attributes?.formatter">
                      <template v-if="attributes?.handleOpen">
                        <a
                          v-if="!isEmpty(model)"
                          :href="attributes.handleOpen(fullObjects[field.name])"
                          class="text-primary"
                          target="_blank"
                        >
                          {{ attributes?.formatter(fullObjects[field.name]) }}
                        </a>
                        <template v-else>-</template>
                      </template>
                      <template v-else>
                        {{ isEmpty(model) ? '-' : attributes?.formatter(fullObjects[field.name]) }}
                      </template>
                    </template>
                    <template v-else>
                      {{ isEmpty(model) ? '-' : model }}
                    </template>
                  </template>
                </b-skeleton-wrapper>
              </template>
            </FormulateInput>
          </b-col>
        </b-row>
        <hr />
        <h5>Ketentuan Kontrak</h5>
        <b-row>
          <b-col v-for="field in contractRequirementFields" :key="field.key" md="6" lg="4">
            <FormulateInput
              v-bind="field"
              :fullObject="fullObjects[field.name]"
              @update:fullObject="($event) => (fullObjects[field.name] = $event)"
              @input="field?.onInput && field.onInput($event)"
            >
              <template #element="{ attributes, model }">
                <b-skeleton-wrapper :loading="isLoading">
                  <template #loading>
                    <b-skeleton height="37px" />
                  </template>
                  <template v-if="isNotEditable || attributes?.readOnly">
                    <template v-if="isEmpty(getFieldDisplayValue(field, model))">
                      <i v-if="attributes?.helper" class="text-muted">{{ attributes?.helper }}</i>
                      <template v-else>-</template>
                    </template>
                    <template v-else>
                      {{ getFieldDisplayValue(field, model) }}
                    </template>
                  </template>
                </b-skeleton-wrapper>
              </template>
              <template #suffix="{ attributes }">
                <template v-if="!isLoading">
                  <div v-if="attributes?.suffixField" class="__suffix">
                    <FormulateInput
                      class="p-0"
                      v-bind="attributes.suffix"
                      :fullObject="fullObjects[attributes.suffix.name]"
                      @update:fullObject="
                        ($event) => (fullObjects[attributes.suffix.name] = $event)
                      "
                    />
                  </div>
                  <div v-else class="formulate-input-suffix">
                    {{ attributes?.suffix }}
                  </div>
                </template>
              </template>
            </FormulateInput>
          </b-col>
        </b-row>
        <hr />
        <h5>Lainnya</h5>
        <b-row>
          <b-col v-for="field in otherFields" :key="field.key" md="6" lg="8">
            <FormulateInput
              v-bind="field"
              :fullObject="fullObjects[field.name]"
              @update:fullObject="($event) => (fullObjects[field.name] = $event)"
            >
              <template #element="{ attributes, model }">
                <b-skeleton-wrapper :loading="isLoading">
                  <template #loading>
                    <b-skeleton height="68px" />
                  </template>
                  <template v-if="isNotEditable || attributes?.readOnly">
                    {{ isEmpty(model) ? '-' : model }}
                  </template>
                </b-skeleton-wrapper>
              </template>
            </FormulateInput>
          </b-col>
        </b-row>
      </b-card>
    </FormulateForm>

    <Attachments
      ref="attachmentsRef"
      :isCreatingContract="isCreatingContract"
      :canEditFields="['Draf', 'Dikonfirmasi'].includes(customerContractStatus)"
      :disabled="isLoading"
      @attachment-added="reloadAllData"
    />

    <VehiclesTable
      ref="vehiclesTableRef"
      :isCreatingContract="isCreatingContract"
      :globalDisabled="isLoading"
      @vehicle-added="reloadAllData"
    >
      <template #[`usage_percentage`]="{ value }">
        {{ (value ?? '') === '' ? '-' : value }}
      </template>
      <template #[`return_date`]="{ value }">
        <template v-if="value">
          <div>{{ formatIndexDate(value).date }}</div>
        </template>
        <template v-else> - </template>
      </template>
      <template #[`status.name`]="{ item, value }">
        <span
          v-if="value"
          class="px-2 py-1"
          :style="{
            backgroundColor: item.status?.background_color,
            color: item.status?.foreground_color,
            borderRadius: '4px',
          }"
        >
          {{ value }}
        </span>
        <template v-else> - </template>
      </template>
    </VehiclesTable>

    <ShareTrackModal :detailData="dataTrackLink" />

    <RenewalContractModal
      v-if="['Dikonfirmasi', 'Kadaluwarsa', 'Ditutup'].includes(customerContractStatus)"
      ref="mc-modal-renewal-contract"
    />
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import Layout from '@/router/layouts/main'
import { mapGetters, mapState } from 'vuex'
import { isEmpty } from 'lodash'
import { diffObject } from '@/helpers/resourceful/compare'
import { formatIndexDate } from '@/helpers/tzDate'

import PageHeaderV2 from '@/components/page-header-v2'
import McButton from '@/components/McButton'
import McWizard from '@/components/McWizard'
import McTransitionStatus from '@/components/McTransitionStatus'

import Attachments from './components/Attachments'
import VehiclesTable from './components/VehiclesTable'
import ShareTrackModal from './components/ShareTrackModal'
import OtherOptions from './components/OtherOptions'
import RenewalContractModal from './components/RenewalContractModal'

import { wizardOrderItems, wizardStatusItems } from './lib/constants/wizard-status-items'
import statusTransitions from './lib/constants/status-transitions'
import { CONTRACT_TYPE } from './lib/constants/contract_type'
import { CONTRACT_PERIOD } from './lib/constants/contract_period'
import apiErrorMappings from './lib/constants/api-error-mappings'

import fields from './lib/fields/form-fields'

import {
  transformCustomerContractStoreRequest,
  transformCustomerContractShowResponse,
  transformCustomerContractUpdateRequest,
} from './lib/helpers/transform-data'

import * as CustomerContract from '@/services/rest-api/lib/contract/customer-contract'

export default {
  page: {
    title: 'Customer Contract Form',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeaderV2,
    McButton,
    McWizard,
    McTransitionStatus,
    Attachments,
    VehiclesTable,
    ShareTrackModal,
    OtherOptions,
    RenewalContractModal,
  },
  mixins: [fields],
  data() {
    return {
      isLoading: false,
      isNotEditable: false,

      dataTrackLink: null,

      formErrors: {},
      formValues: {
        contract_duration: 1,
        contract_period: 'MONTHLY',
      },
      fullObjects: {
        contract_period: {
          key: 'MONTHLY',
          name: 'Bulan',
        },
      },
    }
  },
  computed: {
    ...mapState('masterCustomerContract', ['customerContract']),
    ...mapGetters('masterCustomerContract', ['customerContractStatusName', 'contractNumber']),
    title() {
      return this.isCreatingContract
        ? 'Tambah Kontrak Pelanggan Baru'
        : `Detail Kontrak Pelanggan ${this.contractNumber}`
    },
    breadcrumbItems() {
      return [
        {
          text: 'Data Master',
          to: '/master',
        },
        {
          text: 'Kontrak',
          to: '/master/contract?customer',
        },
        {
          text: this.isCreatingContract
            ? 'Tambah Kontrak Pelanggan Baru'
            : 'Detail Kontrak Pelanggan',
        },
      ]
    },
    wizardItems() {
      if (this.customerContractStatus === 'Dibatalkan') {
        return this.customerContract?.status?.visited_statuses.map((status) => {
          return wizardStatusItems[status]
        })
      }
      return wizardOrderItems[this.customerContractStatus]
    },
    isCreatingContract() {
      const { contractId } = this.$route.params
      return contractId === undefined
    },
    contractId() {
      return this.$route.params.contractId
    },
    customerContractStatus() {
      return this.isCreatingContract ? 'Temporary' : this.customerContractStatusName || 'Temporary'
    },
    statusTransitions() {
      return statusTransitions
    },
    hasChanged() {
      if (this.isLoading) return false

      let { expired_date, ...currentFormValues } = this.formValues
      let oldFormValues = {}

      if (this.isCreatingContract) {
        oldFormValues = {
          contract_duration: 1,
          contract_period: 'MONTHLY',
        }
      } else oldFormValues = { ...transformCustomerContractShowResponse(this.customerContract) }

      const [_, hasChanged] = diffObject(currentFormValues, oldFormValues)

      return hasChanged
    },
    hasTrackLink() {
      return this.customerContract?.public_tracking_url && this.customerContract?.total_vehicle > 0 || false
    },
  },
  watch: {
    contractId: {
      async handler(newId, oldId) {
        if ((newId && oldId === undefined) || (newId && oldId !== newId)) {
          await this.showData()
        }
      },
    },
  },
  mounted() {
    // Fetch contract detail if editing
    if (!this.isCreatingContract && this.contractId) {
      this.showData()
    }
  },
  beforeDestroy() {
    this.$store.commit('masterCustomerContract/SET_CUSTOMER_CONTRACT', null)
  },
  methods: {
    formatIndexDate,
    isEmpty,
    handleApiErrorMessage(apiErrorMappings, formattedErrors) {
      const formErrorsObj = {}
      Object.keys(formattedErrors).map((key) => {
        const msgErrorKey = formattedErrors[key][0]
        if (apiErrorMappings?.[key]?.[msgErrorKey]) {
          formErrorsObj[key] = apiErrorMappings[key][msgErrorKey]
        }
      })

      return formErrorsObj
    },
    async showData() {
      try {
        this.isLoading = true
        const id = this.contractId
        // Call API service directly
        const { data: contract, errors } = await CustomerContract.getDetail(id)

        if (contract) {
          // Update state with the fetched data
          this.$store.commit('masterCustomerContract/SET_CUSTOMER_CONTRACT', contract)

          this.formValues = transformCustomerContractShowResponse(contract)

          this.fullObjects = {
            customer_id: contract?.customer,
            city: {
              name: contract?.city,
            },
            contract_type: CONTRACT_TYPE?.[contract.contract_type],
            custodian_id: contract?.custodian,
            contract_period: CONTRACT_PERIOD?.[contract.contract_period],
            reference: contract?.reference,
          }

          if (errors) {
            let formattedErrors = {}
            Object.keys(errors).map((key) => {
              formattedErrors[key.replace('.', '_')] = errors[key]
            })

            this.formErrors = this.handleApiErrorMessage(apiErrorMappings, formattedErrors)
          }
        }
      } catch (error) {
        console.error('Failed to fetch contract detail:', error)
        this.$mcToast.error('Gagal memuat data kontrak')
      } finally {
        this.isLoading = false
      }
    },
    handleCancel() {
      this.$refs['mc-modal-cancel'].hide()
      if (this.isCreatingContract) {
        this.formValues = {
          contract_duration: 1,
          contract_period: 'MONTHLY',
        }
        this.fullObjects = {
          contract_period: {
            key: 'MONTHLY',
            name: 'Bulan',
          },
        }
      } else {
        this.formValues = transformCustomerContractShowResponse(this.customerContract)
        this.fullObjects = {
          customer_id: this.customerContract?.customer,
          city: {
            name: this.customerContract?.city,
          },
          contract_type: CONTRACT_TYPE?.[this.customerContract?.contract_type],
          custodian_id: this.customerContract?.custodian,
          contract_period: CONTRACT_PERIOD?.[this.customerContract?.contract_period],
        }
      }
    },
    async handleSubmit() {
      const data = this.formValues
      this.isLoading = true
      try {
        if (this.$refs.formRef.hasErrors) {
          this.$refs?.formRef?.showErrors()
          this.$mcToast.error(this.$t('global.inputFormatError'))
          return
        }

        let result

        if (this.isCreatingContract) {
          const payload = transformCustomerContractStoreRequest(data)

          result = await CustomerContract.store(payload)
        } else {
          const oldData = transformCustomerContractShowResponse(this.customerContract)
          const payload = transformCustomerContractUpdateRequest(data, oldData)

          result = await CustomerContract.update(this.contractId, payload)
        }

        if (!result) {
          this.$mcToast.error('Gagal menyimpan data')
          return
        }

        if (result?.errors) {
          this.$mcToast.error('Terdapat kesalahan dalam input anda')
          let formattedErrors = {}
          Object.keys(result.errors).map((key) => {
            formattedErrors[key.replace('.', '_')] = result.errors[key]
          })

          this.formErrors = this.handleApiErrorMessage(apiErrorMappings, formattedErrors)

          const currentContractPeriod =
            data.contract_period ?? this.customerContract?.contract_period
          this.formValues.contract_period = CONTRACT_PERIOD?.[currentContractPeriod]?.key

          return
        } else {
          this.formErrors = {}
        }

        if (this.isCreatingContract && result?.data?.id) {
          this.$mcToast.success({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  tag: 'span',
                  text: 'Kontrak',
                },
                {
                  tag: 'strong',
                  text: result.data.number,
                },
                {
                  tag: 'span',
                  text: 'berhasil dibuat dan disimpan sebagai',
                },
                {
                  tag: 'strong',
                  text: 'Draf',
                },
              ],
            },
          })

          // Navigate to detail page
          this.$router.push({
            name: 'Master Customer Contract Detail',
            params: {
              contractId: result.data.id,
            },
          })
          return
        } else {
          this.$mcToast.success({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  tag: 'span',
                  text: 'Kontrak',
                },
                {
                  tag: 'strong',
                  text: this.customerContract.number,
                },
                {
                  tag: 'span',
                  text: 'berhasil disimpan',
                },
              ],
            },
          })

          this.showData()
        }
      } catch (error) {
        console.error('Submit error:', error)

        this.$mcToast.error('Gagal menyimpan kontrak pelanggan')
      } finally {
        this.isLoading = false
      }
    },
    handleTrackVehicle() {
      this.dataTrackLink = this.customerContract || {}
      this.$bvModal.show('modal-link')
    },
    async handleTransitionStatus(data) {
      try {
        this.isLoading = true
        const res = await CustomerContract.transition(this.contractId, {
          status: data.transition.status.id,
        })

        if (res.errors) {
          this.$mcToast.error('Gagal melakukan transisi status kontrak')
          return
        }

        this.$mcToast.success({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                tag: 'span',
                text: 'Kontrak',
              },
              {
                tag: 'strong',
                text: this.customerContract.number,
              },
              {
                tag: 'span',
                text: `berhasil ${data.transition.status.name}`,
              },
            ],
          },
        })

        await this.reloadAllData()
      } catch (e) {
        this.$mcToast.error('Gagal melakukan transisi status kontrak')
      } finally {
        this.isLoading = false
      }
    },
    async reloadAllData() {
      try {
        await Promise.all([
          this.showData(),
          this.$refs.vehiclesTableRef?.getData?.({
            limit: 10,
            page: 1,
          }),
          this.$refs.attachmentsRef?.getData?.(),
        ])
      } catch (e) {
        window.location.reload()
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.customer-contract-form-layout {
  :deep(.page-content) {
    padding: 70px 0 60px 0;
  }

  :deep(.__suffix .formulate-input) {
    .multiselect__tags {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      width: 100px;
      border: 1px solid $mep-primary !important;
    }

    .multiselect__single {
      color: $mep-primary;
      font-weight: 500;
    }

    .multiselect__content-wrapper {
      min-height: 180px !important;
    }
  }

  :deep(.formulate-input .formulate-input-element) > textarea {
    min-height: 68px !important;
  }
}

.mc-fa {
  font-size: 16px;
}
</style>
